import { formatDate, formatExact, formatPercent } from './formatNumber';
import { matchesHint } from './fieldHints';
import type { SchemaField } from '../types';

/**
 * Metadatos de columna de un widget de tabla.
 *
 * <b>La tabla no se deriva del resultado SQL.</b> Ese es el origen del problema que
 * se corrige: hoy los encabezados son el nombre crudo de la columna de base de
 * datos — `STOCK_ACTUAL`, `DIAS_SIN_ENTRADA` — así que el widget habla el idioma
 * del esquema y no el del gerente que lo lee.
 *
 * Se declaran en `config_schema.columns`.
 */
export type ColumnType = 'text' | 'number' | 'currency' | 'percent' | 'date' | 'badge';

export interface TableColumn {
  /** Nombre real del campo en el resultado. */
  key: string;
  /** Lo que ve el usuario. Obligatorio en columnas declaradas. */
  label: string;
  type: ColumnType;
  /** Por defecto: `right` para number/currency/percent. */
  align?: 'left' | 'right';
  /** Peso relativo del ancho, no píxeles. */
  width?: number;
  /** Una sola por tabla: la que identifica la fila. */
  primary?: boolean;
  /** Contra qué campo se compara para el énfasis semántico. */
  compare?: { against: string; direction: 'below' | 'above' };
  /** Se oculta en el widget compacto y reaparece al ampliar. */
  hideOnCompact?: boolean;
  /** Si la columna se dedujo del dato en vez de declararse. */
  derived?: boolean;
}

/**
 * Nombres que delatan una fecha. Se comparan como PALABRA (ver `fieldHints`), no como substring.
 *
 * `dias` NO está en la lista a propósito: en plural es un conteo (`DIAS_SIN_ENTRADA`,
 * `dias_vencido`), no una fecha. `dia` en singular sí lo es.
 */
const DATE_HINTS = ['fecha', 'date', 'dia', 'creado', 'created', 'actualizado', 'updated', 'emision', 'vencimiento'] as const;
/** Nombres que delatan un importe. */
const MONEY_HINTS = ['valor', 'total', 'precio', 'costo', 'importe', 'monto', 'saldo', 'amount', 'price'] as const;
/**
 * Nombres que delatan un porcentaje.
 *
 * `tasa` NO está: una tasa de cambio (la TRM) es un importe, no un porcentaje, y con `tasa` en la
 * lista `tasa_cambio` se pintaba como «3.118,0 %».
 */
const PERCENT_HINTS = ['porcentaje', 'percent', 'pct', 'margen'] as const;
/**
 * Nombres que delatan un CONTEO, y que ganan a las pistas de importe.
 *
 * Sin esto, `total_registros` casaba con `total` y un conteo de veinte documentos se pintaba
 * como «$ 20».
 */
const COUNT_HINTS = ['cantidad', 'cantidades', 'conteo', 'registros', 'documentos', 'items', 'lineas', 'unidades', 'numero', 'count', 'qty', 'quantity'] as const;

/** Una cadena que empieza por una fecha ISO (`2026-08-19`, `2026-08-19T10:00:00Z`). */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * Convierte `STOCK_ACTUAL` en `Stock actual`.
 *
 * <b>Es un último recurso, no la solución.</b> Funciona hasta que aparece
 * `NIT_TERCERO_FACT`, que no hay heurística que arregle. Existe porque los widgets
 * ya sembrados no declaran columnas y no pueden quedarse mostrando el esquema en
 * pantalla; los nuevos deben declarar su `label` en el Widget Builder.
 */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Deduce el tipo de una columna a partir de su nombre y de una muestra de valores.
 *
 * <b>El DATO manda sobre el NOMBRE, y ese orden es el arreglo.</b> Antes se miraba primero el
 * nombre por substring, así que `DIAS_SIN_ENTRADA` casaba con `dia`, se tipaba como fecha y la
 * celda pintaba <b>«1 ene 1970»</b> para un conteo de días — en el widget que se llama «Stock
 * crítico», y justo en la columna que el ejemplo de este archivo cita.
 *
 * Un valor numérico no es una fecha se llame como se llame el campo. El nombre solo decide
 * cuando el dato no es concluyente.
 */
export function inferColumnType(key: string, sampleValues: unknown[]): ColumnType {
  const firstDefined = sampleValues.find(v => v != null);

  const esCadenaNumerica = typeof firstDefined === 'string'
    && firstDefined.trim() !== ''
    && !ISO_DATE.test(firstDefined)
    && !Number.isNaN(Number(firstDefined));

  // Numérico (incluida la cadena numérica de los endpoints que serializan todo como string).
  if (typeof firstDefined === 'number' || esCadenaNumerica) {
    // Los conteos ganan a las pistas de importe: `total_registros` es un número, no `$ 20`.
    if (matchesHint(key, COUNT_HINTS)) return 'number';
    if (matchesHint(key, PERCENT_HINTS)) return 'percent';
    if (matchesHint(key, MONEY_HINTS)) return 'currency';
    return 'number';
  }

  if (typeof firstDefined === 'boolean') return 'badge';

  // Sin dato numérico, decide el nombre; y una cadena ISO es fecha aunque el campo no se llame así.
  if (matchesHint(key, DATE_HINTS)) return 'date';
  if (typeof firstDefined === 'string' && ISO_DATE.test(firstDefined)) return 'date';
  if (matchesHint(key, PERCENT_HINTS)) return 'percent';
  if (matchesHint(key, MONEY_HINTS)) return 'currency';

  return 'text';
}

/** Alineación por defecto: los números a la derecha, para poder comparar magnitudes. */
export function defaultAlign(type: ColumnType): 'left' | 'right' {
  return type === 'number' || type === 'currency' || type === 'percent' ? 'right' : 'left';
}

/** Traduce el `format` del envelope al tipo de columna de la tabla. */
function typeFromSchemaField(field: SchemaField): ColumnType {
  if (field.format === 'currency') return 'currency';
  if (field.format === 'percent') return 'percent';
  if (field.type === 'date') return 'date';
  if (field.type === 'boolean') return 'badge';
  if (field.type === 'number') return 'number';
  return 'text';
}

/**
 * Las columnas de una tabla, resueltas en tres capas de prioridad.
 *
 * ```
 * 1. config_schema.columns   lo que declara el widget — manda siempre
 * 2. schema.fields           lo que declara el ENDPOINT en su envelope
 * 3. deducción del dato      último recurso: humanizar el nombre del campo
 * ```
 *
 * <b>La capa 2 es la que resuelve el problema de verdad, y estaba ahí sin usarse.</b>
 * El envelope estándar ya trae `label`, `type` y `format` por campo — el endpoint de
 * stock crítico declara "Existencia", "Mínimo" y "Días sin entrada" — pero la tabla
 * los ignoraba y pintaba el nombre crudo de la columna de base de datos.
 *
 * Consumirlos tiene dos ventajas sobre sembrar `config_schema` en cada host: las
 * etiquetas viven junto a la consulta que las produce, y funciona para <b>cualquier
 * host</b> que devuelva el envelope, sin configurar nada.
 *
 * `visibleColumns` del Widget Builder se sigue respetando como filtro y orden, para
 * no cambiarle la tabla a nadie que ya la haya configurado.
 */
export function resolveTableColumns(
  declared: TableColumn[] | undefined,
  rows: Record<string, unknown>[],
  visibleColumns?: string[],
  schemaFields?: SchemaField[],
): TableColumn[] {
  const availableKeys = rows.length > 0 ? Object.keys(rows[0]) : [];

  // Capa 1 — lo que declara el widget.
  if (declared && declared.length > 0) {
    const disponibles: TableColumn[] = declared
      .filter(col => availableKeys.length === 0 || availableKeys.includes(col.key))
      .map(col => ({ ...col, align: col.align ?? defaultAlign(col.type) }));

    /**
     * `visibleColumns` filtra y ordena TAMBIÉN sobre las columnas declaradas.
     *
     * Es lo que el JSDoc de esta función promete —"se sigue respetando como filtro y orden, para
     * no cambiarle la tabla a nadie que ya la haya configurado"— y la capa 1 lo estaba ignorando:
     * un usuario que había elegido 3 de 8 columnas volvía a ver las 8 en cuanto el widget
     * declaraba `config_schema.columns`.
     */
    if (!visibleColumns?.length) return disponibles;
    const porClave = new Map(disponibles.map(c => [c.key, c]));
    return visibleColumns
      .map(k => porClave.get(k))
      .filter((c): c is TableColumn => Boolean(c));
  }

  const keys = visibleColumns?.length
    ? visibleColumns.filter(k => availableKeys.includes(k))
    : availableKeys;

  const byName = new Map((schemaFields ?? []).map(field => [field.name, field]));

  return keys.map(key => {
    // Capa 2 — lo que declara el endpoint.
    const field = byName.get(key);
    if (field) {
      const type = typeFromSchemaField(field);
      return { key, label: field.label || humanizeKey(key), type, align: defaultAlign(type) };
    }

    // Capa 3 — deducción.
    const type = inferColumnType(key, rows.slice(0, 5).map(row => row[key]));
    return { key, label: humanizeKey(key), type, align: defaultAlign(type), derived: true };
  });
}

/**
 * Si una celda cumple la condición que da nombre al widget.
 *
 * Una tabla llamada "Stock crítico" tiene que mostrar QUÉ está crítico: hoy un `3`
 * contra un mínimo de `10` se ve igual que cualquier otro número.
 */
export function meetsCompare(
  column: TableColumn,
  row: Record<string, unknown>,
): boolean {
  if (!column.compare) return false;
  const value = Number(row[column.key]);
  const threshold = Number(row[column.compare.against]);
  if (!Number.isFinite(value) || !Number.isFinite(threshold)) return false;
  return column.compare.direction === 'below' ? value < threshold : value > threshold;
}

/**
 * Etiquetas de un valor booleano, ya traducidas por quien tiene el hook.
 *
 * `formatCellValue` es una función pura y no puede llamar a `useDashboardTranslation`, así que
 * las recibe. Antes devolvía `'Sí'`/`'No'` cableados en español, y una tabla con una columna
 * booleana los mostraba tal cual en un host en inglés.
 */
export interface CellLabels {
  yes: string;
  no: string;
}

/** El texto de una celda, según el tipo declarado de su columna. */
export function formatCellValue(
  value: unknown,
  type: ColumnType,
  currency: string | null,
  labels?: CellLabels,
): string {
  if (value == null || value === '') return '—';

  switch (type) {
    case 'currency':
      // Sin abreviar: una tabla existe para comparar cifras exactas.
      return formatExact(Number(value), { currency });
    case 'number':
      return formatExact(Number(value));
    case 'percent':
      return formatPercent(Number(value));
    case 'date':
      return formatDate(value as string);
    case 'badge':
      // Los respaldos son el comportamiento anterior, para un consumidor que aún no pase labels.
      if (typeof value === 'boolean') return value ? (labels?.yes ?? 'Sí') : (labels?.no ?? 'No');
      return String(value);
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}
